import { Company } from '../../types';
import { normalizeRolesList, normalizeRoleColorsMap } from '../../utils/textFormat';
import { ensureCompany } from './companyService';
import { loadColumns } from './columnService';
import { loadEmployees, seedCompanyStructure } from './employeeService';
import {
  loadRoles,
  loadRoleColors,
  loadValidatedSetting,
  loadSharedCostCenters,
} from './settingsService';
import { rhAfastamentoDatesSettingSchema } from '../rhDataSchemas';
import type { CompanyData, DbClient } from './types';

/**
 * Carrega snapshot completo da empresa para o board (colunas, colaboradores, settings).
 * @param sb - Cliente API
 * @param company - Nome da empresa
 */
export async function loadFullCompanyData(sb: DbClient, company: Company): Promise<CompanyData> {
  const companyId = await ensureCompany(sb, company);

  const [{ columns, hiringLimits }, { items, columnKeyMap }] = await Promise.all([
    loadColumns(sb, companyId),
    loadEmployees(sb, companyId),
  ]);

  if (columns.length === 0) {
    await seedCompanyStructure(sb, company, companyId);
    return loadFullCompanyData(sb, company);
  }

  const [rolesRaw, roleColorsRaw, afastamentoDatesRaw, costCenters] = await Promise.all([
    loadRoles(sb, companyId),
    loadRoleColors(sb, companyId),
    loadValidatedSetting(sb, 'afastamento_dates', rhAfastamentoDatesSettingSchema, companyId),
    loadSharedCostCenters(sb, companyId),
  ]);

  const availableRoles = normalizeRolesList(rolesRaw ?? []);
  const roleColors = normalizeRoleColorsMap(roleColorsRaw ?? {});
  const afastamentoDates = afastamentoDatesRaw ?? {};

  return {
    companyId,
    items,
    columns,
    hiringLimits,
    availableRoles,
    roleColors,
    columnKeyMap,
    afastamentoDates,
    costCenters,
  };
}
